import React, { ReactElement, SyntheticEvent, createElement } from 'react';
import { TimeoutID, cancelTimeout, requestTimeout } from './timer';
import { getRTLOffsetType, getScrollbarSize } from './domHelpers';
import { ReactElementType } from 'react-window';
import memoizeOne from 'memoize-one';

// version 1.8.10 https://github.com/bvaughn/react-window

export type ScrollToAlign = 'auto' | 'smart' | 'center' | 'start' | 'end';

type itemSize = number | ((index: number) => number);
type Direction = 'ltr' | 'rtl' | 'horizontal' | 'vertical';
type Layout = 'horizontal' | 'vertical';

type onItemsRenderedCallbackProps = {
  overscanStartIndex: number;
  overscanStopIndex: number;
  visibleStartIndex: number;
  visibleStopIndex: number;
};

type onItemsRenderedCallback = ({
  overscanStartIndex,
  overscanStopIndex,
  visibleStartIndex,
  visibleStopIndex
}: onItemsRenderedCallbackProps) => void;

type onScrollCallbackProps = {
  scrollDirection: ScrollDirection;
  scrollOffset: number;
  scrollUpdateWasRequested: boolean;
};

type onScrollCallback = ({
  scrollDirection,
  scrollOffset,
  scrollUpdateWasRequested
}: onScrollCallbackProps) => void;

type RenderComponentProps<T> = {
  data: T;
  index: number;
  style: React.CSSProperties;
  isScrolling?: boolean;
  className?: string;
  cssId?: string;
};

type ScrollEvent = SyntheticEvent<HTMLDivElement>;
type ItemStyleCache = { [index: number]: React.CSSProperties };

type RenderComponent<T> = React.ComponentType<RenderComponentProps<T>>;

type ScrollDirection = 'forward' | 'backward';

type InstanceProps = unknown;

type State = {
  instance: unknown;
  isScrolling: boolean;
  scrollDirection: ScrollDirection;
  scrollOffset: number;
  scrollUpdateWasRequested: boolean;
};

type GetItemOffset<T> = {
  (props: Props<T>, index: number, instanceProps: InstanceProps): number;
};
type GetItemSize<T> = {
  (props: Props<T>, index: number, instanceProps: InstanceProps): number;
};
type GetEstimatedTotalSize<T> = {
  (props: Props<T>, instanceProps: InstanceProps): number;
};
type GetOffsetForIndexAndAlignment<T> = {
  (
    props: Props<T>,
    index: number,
    align: ScrollToAlign,
    scrollOffset: number,
    instanceProps: InstanceProps,
    scrollbarSize: number
  ): number;
};
type GetStartIndexForOffset<T> = {
  (props: Props<T>, offset: number, instanceProps: InstanceProps): number;
};
type GetStopIndexForStartIndex<T> = {
  (props: Props<T>, startIndex: number, scrollOffset: number, instanceProps: InstanceProps): number;
};
type InitInstanceProps<T> = {
  (props: Props<T>, instanceProps: InstanceProps): unknown;
};
type ValidateProps<T> = {
  (props: Props<T>): void;
};

const IS_SCROLLING_DEBOUNCE_INTERVAL = 150;

const defaultItemKey = (index: number): number => index;

export type Props<T> = {
  children: RenderComponent<T>;
  childrenClassName?: string;
  cssId?: string;
  className?: string;
  direction: Direction;
  height: number | string;
  initialScrollOffset?: number;
  innerRef?: unknown; // ts-ignore
  innerElementType?: string | ReactElementType;
  innerTagName?: string; // deprecated
  itemCount: number;
  itemData: T;
  itemKey?: (index: number, data: T) => number;
  itemSize: itemSize;
  layout: Layout;
  onItemsRendered?: onItemsRenderedCallback;
  onScroll?: onScrollCallback;
  outerRef?: unknown;
  outerElementType?: string | ReactElementType;
  outerTagName?: string; // deprecated
  overscanCount: number;
  style?: React.CSSProperties;
  useIsScrolling: boolean;
  width: number | string;
};

export class List<T> extends React.Component<Props<T>, State> {
  getItemOffset;
  getItemSize;
  _instanceProps;
  shouldResetStyleCacheOnItemSizeChange;
  getStartIndexForOffset;
  getStopIndexForStartIndex;
  validateProps;
  getEstimatedTotalSize;
  getOffsetForIndexAndAlignment;
  _outerRef: HTMLDivElement | undefined;
  _resetIsScrollingTimeoutId: TimeoutID | null = null;
  _getItemStyleCache: (_: unknown, __: unknown, ___: unknown) => ItemStyleCache;

  state: State = {
    instance: this,
    isScrolling: false,
    scrollDirection: 'forward',
    scrollOffset:
      typeof this.props.initialScrollOffset === 'number' ? this.props.initialScrollOffset : 0,
    scrollUpdateWasRequested: false
  };

  static defaultProps = {
    direction: 'ltr',
    itemData: undefined,
    layout: 'vertical',
    overscanCount: 2,
    useIsScrolling: false
  };

  constructor(
    props: Props<T>,
    getItemOffset: GetItemOffset<T>,
    getEstimatedTotalSize: GetEstimatedTotalSize<T>,
    getItemSize: GetItemSize<T>,
    getOffsetForIndexAndAlignment: GetOffsetForIndexAndAlignment<T>,
    getStartIndexForOffset: GetStartIndexForOffset<T>,
    getStopIndexForStartIndex: GetStopIndexForStartIndex<T>,
    initInstanceProps: InitInstanceProps<T>,
    shouldResetStyleCacheOnItemSizeChange: boolean,
    validateProps: ValidateProps<T>
  ) {
    super(props);
    this.getItemOffset = getItemOffset;
    this.getItemSize = getItemSize;
    this._instanceProps = initInstanceProps(this.props, this);
    this.shouldResetStyleCacheOnItemSizeChange = shouldResetStyleCacheOnItemSizeChange;
    this.getStartIndexForOffset = getStartIndexForOffset;
    this.getStopIndexForStartIndex = getStopIndexForStartIndex;
    this.validateProps = validateProps;
    this.getEstimatedTotalSize = getEstimatedTotalSize;
    this.getOffsetForIndexAndAlignment = getOffsetForIndexAndAlignment;
    this._getItemStyleCache = memoizeOne(() => ({}));
  }

  scrollTo(scrollOffset: number): void {
    const newScrollOffset = Math.max(0, scrollOffset);

    this.setState((prevState) => {
      if (prevState.scrollOffset === newScrollOffset) {
        return null;
      }
      return {
        scrollDirection: prevState.scrollOffset < newScrollOffset ? 'forward' : 'backward',
        scrollOffset: newScrollOffset,
        scrollUpdateWasRequested: true
      };
    }, this._resetIsScrollingDebounced);
  }

  scrollToItem(index: number, align: ScrollToAlign = 'auto'): void {
    const { itemCount, layout } = this.props;
    const { scrollOffset } = this.state;
    const newIndex = Math.max(0, Math.min(index, itemCount - 1));

    // The scrollbar size should be considered when scrolling an item into view, to ensure it's fully visible.
    // But we only need to account for its size when it's actually visible.
    // This is an edge case for lists; normally they only scroll in the dominant direction.
    let scrollbarSize = 0;
    if (this._outerRef) {
      const outerRef = this._outerRef as HTMLElement;
      if (layout === 'vertical') {
        scrollbarSize = outerRef.scrollWidth > outerRef.clientWidth ? getScrollbarSize() : 0;
      } else {
        scrollbarSize = outerRef.scrollHeight > outerRef.clientHeight ? getScrollbarSize() : 0;
      }
    }

    this.scrollTo(
      this.getOffsetForIndexAndAlignment(
        this.props,
        newIndex,
        align,
        scrollOffset,
        this._instanceProps,
        scrollbarSize
      )
    );
  }

  componentDidMount(): void {
    const { direction, initialScrollOffset, layout } = this.props;

    if (typeof initialScrollOffset === 'number' && this._outerRef != null) {
      const outerRef = this._outerRef as HTMLElement;
      if (direction === 'horizontal' || layout === 'horizontal') {
        outerRef.scrollLeft = initialScrollOffset;
      } else {
        outerRef.scrollTop = initialScrollOffset;
      }
    }

    this._callPropsCallbacks();
  }

  componentDidUpdate(): void {
    const { direction, layout } = this.props;
    const { scrollOffset, scrollUpdateWasRequested } = this.state;

    if (scrollUpdateWasRequested && this._outerRef != null) {
      const outerRef = this._outerRef as HTMLElement;

      if (direction === 'horizontal' || layout === 'horizontal') {
        if (direction === 'rtl') {
          // TRICKY According to the spec, scrollLeft should be negative for RTL aligned elements.
          // This is not the case for all browsers though (e.g. Chrome reports values as positive, measured relative to the left).
          // So we need to determine which browser behavior we're dealing with, and mimic it.
          switch (getRTLOffsetType()) {
            case 'negative':
              outerRef.scrollLeft = -scrollOffset;
              break;
            case 'positive-ascending':
              outerRef.scrollLeft = scrollOffset;
              break;
            default:
              const { clientWidth, scrollWidth } = outerRef;
              outerRef.scrollLeft = scrollWidth - clientWidth - scrollOffset;
              break;
          }
        } else {
          outerRef.scrollLeft = scrollOffset;
        }
      } else {
        outerRef.scrollTop = scrollOffset;
      }
    }

    this._callPropsCallbacks();
  }

  componentWillUnmount(): void {
    if (this._resetIsScrollingTimeoutId !== null) {
      cancelTimeout(this._resetIsScrollingTimeoutId);
    }
  }

  render(): ReactElement {
    const {
      children,
      className,
      direction,
      height,
      innerRef,
      innerElementType,
      innerTagName,
      itemCount,
      itemData,
      itemKey = defaultItemKey,
      layout,
      outerElementType,
      outerTagName,
      style,
      useIsScrolling,
      childrenClassName,
      cssId
    } = this.props;

    const { isScrolling } = this.state;

    const isHorizontal = direction === 'horizontal' || layout === 'horizontal';

    const onScroll = isHorizontal ? this._onScrollHorizontal : this._onScrollVertical;

    const [startIndex, stopIndex] = this._getRangeToRender();

    const items: ReactElement[] = [];
    if (itemCount > 0) {
      for (let index = startIndex; index <= stopIndex; index++) {
        items.push(
          createElement(children, {
            data: itemData,
            key: itemKey(index, itemData),
            index,
            isScrolling: useIsScrolling ? isScrolling : undefined,
            style: this._getItemStyle(index),
            className: childrenClassName ?? '',
            cssId: cssId ?? ''
          })
        );
      }
    }

    const estimatedTotalSize = this.getEstimatedTotalSize(this.props, this._instanceProps);

    return createElement(
      outerElementType || outerTagName || 'div',
      {
        className,
        onScroll,
        ref: this._outerRefSetter,
        style: {
          position: 'relative',
          height,
          width: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          willChange: 'transform',
          direction,
          ...style
        }
      },
      createElement(
        innerElementType || innerTagName || 'div',
        {
          ref: innerRef,
          style: {
            height: isHorizontal ? '100%' : estimatedTotalSize,
            pointerEvents: isScrolling ? 'none' : undefined,
            width: isHorizontal ? estimatedTotalSize : '100%'
          }
        },
        items
      )
    );
  }

  _outerRefSetter = (ref: unknown): void => {
    const { outerRef } = this.props;

    this._outerRef = ref as HTMLDivElement;

    if (typeof outerRef === 'function') {
      outerRef(ref);
    } else if (
      outerRef != null &&
      typeof outerRef === 'object' &&
      Object.prototype.hasOwnProperty.call(outerRef, 'current')
    ) {
      // @ts-expect-error Should be a component
      outerRef.current = ref;
    }
  };

  _callOnItemsRendered = memoizeOne(
    (
      overscanStartIndex: number,
      overscanStopIndex: number,
      visibleStartIndex: number,
      visibleStopIndex: number
    ) => {
      if (this.props.onItemsRendered) {
        this.props.onItemsRendered({
          overscanStartIndex,
          overscanStopIndex,
          visibleStartIndex,
          visibleStopIndex
        });
      }
    }
  );

  _callOnScroll = memoizeOne(
    (scrollDirection: ScrollDirection, scrollOffset: number, scrollUpdateWasRequested: boolean) => {
      if (this.props.onScroll) {
        this.props.onScroll({ scrollDirection, scrollOffset, scrollUpdateWasRequested });
      }
    }
  );

  _callPropsCallbacks(): void {
    if (typeof this.props.onItemsRendered === 'function') {
      const { itemCount } = this.props;
      if (itemCount > 0) {
        const [overscanStartIndex, overscanStopIndex, visibleStartIndex, visibleStopIndex] =
          this._getRangeToRender();
        this._callOnItemsRendered(
          overscanStartIndex,
          overscanStopIndex,
          visibleStartIndex,
          visibleStopIndex
        );
      }
    }

    if (typeof this.props.onScroll === 'function') {
      const { scrollDirection, scrollOffset, scrollUpdateWasRequested } = this.state;
      this._callOnScroll(scrollDirection, scrollOffset, scrollUpdateWasRequested);
    }
  }

  createStyle = (index: number, direction: Direction, layout: Layout): React.CSSProperties => {
    const offset = this.getItemOffset(this.props, index, this._instanceProps);
    const size = this.getItemSize(this.props, index, this._instanceProps);
    const isHorizontal = direction === 'horizontal' || layout === 'horizontal';

    const isRtl = direction === 'rtl';
    const offsetHorizontal = isHorizontal ? offset : 0;
    return {
      position: 'absolute',
      left: isRtl ? undefined : offsetHorizontal,
      right: isRtl ? offsetHorizontal : undefined,
      top: !isHorizontal ? offset : 0,
      height: !isHorizontal ? size : '100%'
    };
  };

  _getItemStyle = (index: number): React.CSSProperties => {
    const { direction, itemSize, layout } = this.props;
    const shouldResetStyleCacheOnItemSizeChange = this.shouldResetStyleCacheOnItemSizeChange;

    const itemStyleCache = this._getItemStyleCache(
      shouldResetStyleCacheOnItemSizeChange && itemSize,
      shouldResetStyleCacheOnItemSizeChange && layout,
      shouldResetStyleCacheOnItemSizeChange && direction
    );

    let style: React.CSSProperties;
    if (index in itemStyleCache) {
      style = itemStyleCache[index];
    } else {
      itemStyleCache[index] = style = this.createStyle(index, direction, layout);
    }

    return style;
  };

  _getRangeToRender(): [number, number, number, number] {
    const { itemCount, overscanCount } = this.props;
    const { isScrolling, scrollDirection, scrollOffset } = this.state;

    if (itemCount === 0) {
      return [0, 0, 0, 0];
    }

    const startIndex = this.getStartIndexForOffset(this.props, scrollOffset, this._instanceProps);
    const stopIndex = this.getStopIndexForStartIndex(
      this.props,
      startIndex,
      scrollOffset,
      this._instanceProps
    );

    // Overscan by one item in each direction so that tab/focus works.
    // If there isn't at least one extra item, tab loops back around.
    const overscanBackward =
      !isScrolling || scrollDirection === 'backward' ? Math.max(1, overscanCount) : 1;
    const overscanForward =
      !isScrolling || scrollDirection === 'forward' ? Math.max(1, overscanCount) : 1;

    return [
      Math.max(0, startIndex - overscanBackward),
      Math.max(0, Math.min(itemCount - 1, stopIndex + overscanForward)),
      startIndex,
      stopIndex
    ];
  }

  _onScrollHorizontal = (event: ScrollEvent): void => {
    const { clientWidth, scrollLeft, scrollWidth } = event.currentTarget;
    this.setState((prevState) => {
      if (prevState.scrollOffset === scrollLeft) {
        // Scroll position may have been updated by cDM/cDU,
        // In which case we don't need to trigger another render,
        // And we don't want to update state.isScrolling.
        return null;
      }

      const { direction } = this.props;

      let scrollOffset = scrollLeft;
      if (direction === 'rtl') {
        // TRICKY According to the spec, scrollLeft should be negative for RTL aligned elements.
        // This is not the case for all browsers though (e.g. Chrome reports values as positive, measured relative to the left).
        // It's also easier for this component if we convert offsets to the same format as they would be in for ltr.
        // So the simplest solution is to determine which browser behavior we're dealing with, and convert based on it.
        switch (getRTLOffsetType()) {
          case 'negative':
            scrollOffset = -scrollLeft;
            break;
          case 'positive-descending':
            scrollOffset = scrollWidth - clientWidth - scrollLeft;
            break;
        }
      }

      // Prevent Safari's elastic scrolling from causing visual shaking when scrolling past bounds.
      scrollOffset = Math.max(0, Math.min(scrollOffset, scrollWidth - clientWidth));

      return {
        isScrolling: true,
        scrollDirection: prevState.scrollOffset < scrollOffset ? 'forward' : 'backward',
        scrollOffset,
        scrollUpdateWasRequested: false
      };
    }, this._resetIsScrollingDebounced);
  };

  _onScrollVertical = (event: ScrollEvent): void => {
    const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
    this.setState((prevState) => {
      if (prevState.scrollOffset === scrollTop) {
        // Scroll position may have been updated by cDM/cDU,
        // In which case we don't need to trigger another render,
        // And we don't want to update state.isScrolling.
        return null;
      }

      // Prevent Safari's elastic scrolling from causing visual shaking when scrolling past bounds.
      const scrollOffset = Math.max(0, Math.min(scrollTop, scrollHeight - clientHeight));

      return {
        isScrolling: true,
        scrollDirection: prevState.scrollOffset < scrollOffset ? 'forward' : 'backward',
        scrollOffset,
        scrollUpdateWasRequested: false
      };
    }, this._resetIsScrollingDebounced);
  };

  _resetIsScrollingDebounced = (): void => {
    if (this._resetIsScrollingTimeoutId !== null) {
      cancelTimeout(this._resetIsScrollingTimeoutId);
    }

    this._resetIsScrollingTimeoutId = requestTimeout(
      this._resetIsScrolling,
      IS_SCROLLING_DEBOUNCE_INTERVAL
    );
  };

  _resetIsScrolling = (): void => {
    this._resetIsScrollingTimeoutId = null;

    this.setState({ isScrolling: false }, () => {
      // Clear style cache after state update has been committed.
      // This way we don't break pure sCU for items that don't use isScrolling param.
      this._getItemStyleCache(-1, null, null);
    });
  };
}
